import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {format} from 'date-fns';
//UI material
import {makeStyles} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
//DS
const useStyles = makeStyles((theme) => ({
   root: {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.secondary,
      '& svg': {
         margin: theme.spacing(1.2)
      }
   },
   listIcon: {
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0),
      'line-height': '2px',
      '& svg': {
         margin: theme.spacing(0)
      },
      minWidth: '0px',
      minHeight: '0px',
      width: '35px'
   },
   divider: {
      marginRight: theme.spacing(1)
   }
}));
function genPrimaryTextIndividualDefault(item) {
   return `${item.tipo}-${item.id}`;
}
function genSecondaryTextIndividualDefault(item) {
   return format(item.fecha, 'yyyy-MM-dd HH:mm:ss');
}
function genPrimaryTextResumenDefault(tipo, cantidad) {
   return tipo;
}
function genSecondaryTextResumenDefault(tipo, cantidad) {
   return `${cantidad} notificaciones sin leer`;
}
function NotificacionesList({
   tipos: tiposProps,
   notificaciones: notificacionesProps,
   generarPrimaryTextIndividual,
   generarSecondaryTextIndividual,
   generarPrimaryTextResumen,
   generarSecondaryTextResumen,
   onClickLeido,
   onClickIndividual,
   onClickResumen,
   cantidadMinimaResumen,
   Icon,
   ...other
}) {
   const classes = useStyles();
   //#region generar formato de tipos como objeto para agilizar busqueda
   const tipos = useMemo(() => {
      return tiposProps.reduce((prev, curr) => {
         prev[curr.tipo] = {
            tipo: curr.tipo,
            generarPrimaryTextIndividual:
               curr.generarPrimaryTextIndividual ||
               generarPrimaryTextIndividual ||
               genPrimaryTextIndividualDefault,
            generarSecondaryTextIndividual:
               curr.generarSecondaryTextIndividual ||
               generarSecondaryTextIndividual ||
               genSecondaryTextIndividualDefault,
            generarPrimaryTextResumen:
               curr.generarPrimaryTextResumen ||
               generarPrimaryTextResumen ||
               genPrimaryTextResumenDefault,
            generarSecondaryTextResumen:
               curr.generarSecondaryTextResumen ||
               generarSecondaryTextResumen ||
               genSecondaryTextResumenDefault,
            onClickLeido: curr.onClickLeido || onClickLeido,
            onClickIndividual: curr.onClickIndividual || onClickIndividual,
            onClickResumen: curr.onClickResumen || onClickResumen,
            Icon: curr.Icon || Icon
         };
         return prev;
      }, {});
   }, [tiposProps, onClickLeido, onClickIndividual, onClickResumen]);
   //#endregion
   //#region ordenar notificaciones por fecha
   const notificaciones = useMemo(() => {
      const nuevasNotificaciones = [...notificacionesProps].sort((n1, n2) => {
         return n2.fecha.getTime() - n1.fecha.getTime();
      });
      return nuevasNotificaciones;
   }, [notificacionesProps, tipos]);
   //#endregion
   //#region generar metadata de cuantas notificaciones por tipo existen
   const metaTipos = useMemo(() => {
      const nuevaMetaTipos = notificacionesProps.reduce((prev, curr) => {
         if (prev[curr.tipo]) {
            prev[curr.tipo]++;
         } else {
            prev[curr.tipo] = 1;
         }
         return prev;
      }, {});
      return nuevaMetaTipos;
   }, [notificacionesProps]);
   //#endregion
   return (
      <List dense {...other}>
         {
            notificacionesProps.length >= cantidadMinimaResumen
               ? //#region resumen
                 Object.keys(metaTipos).map((item) => {
                    const tipoItem = tipos[item];
                    if (!Boolean(tipoItem)) {
                       return null;
                    }
                    return (
                       <React.Fragment key={item}>
                          <ListItem
                             className={classes.root}
                             button={Boolean(tipoItem.onClickIndividual)}
                             onClick={() => {
                                if (typeof tipoItem.onClickResumen === 'function') {
                                   tipoItem.onClickResumen(item, metaTipos[item]);
                                }
                             }}
                          >
                             {tipoItem.Icon && (
                                <>
                                   <ListItemIcon className={classes.listIcon}>
                                      {<tipoItem.Icon />}
                                   </ListItemIcon>
                                   <Divider
                                      className={classes.divider}
                                      orientation="vertical"
                                      flexItem
                                   />
                                </>
                             )}
                             <ListItemText
                                primary={tipoItem.generarPrimaryTextResumen(item, metaTipos[item])}
                                secondary={tipoItem.generarSecondaryTextResumen(
                                   item,
                                   metaTipos[item]
                                )}
                             />
                          </ListItem>
                          <Divider />
                       </React.Fragment>
                    );
                 })
               : //#endregion
                 //#region individuales
                 notificaciones.map((item) => {
                    const tipoItem = tipos[item.tipo];
                    if (!Boolean(tipoItem)) {
                       return null;
                    }
                    return (
                       <React.Fragment key={item.id}>
                          <ListItem
                             className={classes.root}
                             button={Boolean(tipoItem.onClickIndividual)}
                             onClick={() => {
                                if (typeof tipoItem.onClickIndividual === 'function') {
                                   tipoItem.onClickIndividual(item);
                                }
                             }}
                          >
                             {tipoItem.Icon && (
                                <>
                                   <ListItemIcon className={classes.listIcon}>
                                      {<tipoItem.Icon />}
                                   </ListItemIcon>
                                   <Divider
                                      className={classes.divider}
                                      orientation="vertical"
                                      flexItem
                                   />
                                </>
                             )}
                             <ListItemText
                                primary={tipoItem.generarPrimaryTextIndividual(item)}
                                secondary={tipoItem.generarSecondaryTextIndividual(item)}
                             />
                             {tipoItem.onClickLeido && (
                                <ListItemSecondaryAction>
                                   <IconButton
                                      onClick={() => {
                                         tipoItem.onClickLeido(item);
                                      }}
                                      edge="end"
                                      aria-label="read"
                                      title="Marcar como leido"
                                   >
                                      <CheckCircleOutlineIcon />
                                   </IconButton>
                                </ListItemSecondaryAction>
                             )}
                          </ListItem>
                          <Divider />
                       </React.Fragment>
                    );
                 })
            //#endregion
         }
      </List>
   );
}
NotificacionesList.propTypes = {
   tipos: PropTypes.arrayOf(
      PropTypes.shape({
         tipo: PropTypes.string.isRequired,
         generarPrimaryTextIndividual: PropTypes.func,
         generarSecondaryTextIndividual: PropTypes.func,
         generarPrimaryTextResumen: PropTypes.func,
         generarSecondaryTextResumen: PropTypes.func,
         onClickLeido: PropTypes.func,
         onClickIndividual: PropTypes.func,
         onClickResumen: PropTypes.func,
         Icon: PropTypes.elementType
      })
   ).isRequired,
   notificaciones: PropTypes.arrayOf(
      PropTypes.shape({
         tipo: PropTypes.string.isRequired,
         id: PropTypes.string.isRequired,
         fecha: PropTypes.instanceOf(Date).isRequired
      })
   ).isRequired,
   generarPrimaryTextIndividual: PropTypes.func,
   generarSecondaryTextIndividual: PropTypes.func,
   generarPrimaryTextResumen: PropTypes.func,
   generarSecondaryTextResumen: PropTypes.func,
   onClickLeido: PropTypes.func,
   onClickIndividual: PropTypes.func,
   onClickResumen: PropTypes.func,
   cantidadMinimaResumen: PropTypes.number,
   Icon: PropTypes.elementType
};
NotificacionesList.defaultProps = {
   cantidadMinimaResumen: 10
};
export default NotificacionesList;
